import { LitElement, html, nothing, unsafeCSS, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import {
  listModules,
  resolveVisibleModuleIds,
  WORKFLOW_SECTIONS,
  type ModuleSubmenuItem,
  type ResolvedModule,
  type WorkflowSection,
} from '../../modules/registry/module-registry';
import { getRecentModules } from '../../services/recent-modules-service';
import styles from './workflow-menu.css?inline';
import '../../components/search/agwa-search';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/** A section with its modules resolved and the search applied. */
interface RenderedSection {
  section: WorkflowSection;
  entries: Array<{ module: ResolvedModule; shortcuts: ModuleSubmenuItem[] }>;
}

/**
 * The workflow menu: everything this person can reach, ordered by the kind of work rather than by
 * the module that happens to own it.
 *
 * <p>WHY IT EXISTS BESIDE THE MODULES HUB RATHER THAN REPLACING IT. The hub groups by module and
 * then by category, so a module's settings sit beneath the module, and changing a lookup means
 * first working out which module owns the lookup. AGWA staff do not think in modules; they think in
 * the work. The two are live at once on purpose, so the old one can be retired once this is
 * preferred rather than in the same breath as building it.</p>
 *
 * <p>THE DENSITY COMES FROM MOVING DESCRIPTIONS OFF THE SURFACE, into the info indicator beside
 * each name - the pattern the site operation-type sheet already uses. That is a deliberate trade:
 * a daily user gets the whole product in one view, a first-time user has to hover to learn what
 * something is.</p>
 *
 * <p>WHAT IT DELIBERATELY KEEPS from the hub: the search, the match highlighting, and `/` to reach
 * the search from anywhere on the page. Those are the things a dense list needs most, and losing
 * them in a rewrite would trade one kind of speed for another.</p>
 */
@customElement('workflow-menu')
export class WorkflowMenu extends WithUserProfileConsumer(LitElement) {
  static styles = unsafeCSS(styles);

  @state() private search = '';
  @state() private recent: ResolvedModule[] = [];

  private searchInput?: HTMLElement;

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('keydown', this.onGlobalKeyDown);
  }

  disconnectedCallback(): void {
    window.removeEventListener('keydown', this.onGlobalKeyDown);
    super.disconnectedCallback();
  }

  firstUpdated(): void {
    void this.loadRecent();
  }

  /**
   * `/` reaches the search from anywhere on the page, as it does on the modules hub.
   *
   * Ignored while the caret is already in a field, or the shortcut would eat the slash of anyone
   * typing a route or a date into a search box.
   */
  private onGlobalKeyDown = (event: KeyboardEvent) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }
    const active = document.activeElement as HTMLElement | null;
    if (
      active &&
      (active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.getAttribute('contenteditable') === 'true')
    ) {
      return;
    }
    event.preventDefault();
    this.searchInput?.focus();
  };

  /**
   * The recently visited modules, resolved through the registry's visibility filter.
   *
   * Only the RECORDED IDS are shared with the home page, never its rendering: the same history read
   * through this menu's own condensed shape. The filter is shared deliberately though - see
   * resolveVisibleModuleIds for why a recent entry must never outlive access to what it names.
   */
  private async loadRecent(): Promise<void> {
    const userId = this.userProfile?.userId;
    if (!userId) return;

    const entries = await getRecentModules(userId, 6);
    this.recent = resolveVisibleModuleIds(
      entries.map((entry) => entry.moduleId),
      { access: this.access, isLoggedIn: this.isAuthenticated }
    );
  }

  /** Every module this person may see, keyed by id, so a section is a lookup rather than a scan. */
  private get visibleById(): Map<string, ResolvedModule> {
    return new Map(
      listModules({
        access: this.access,
        isLoggedIn: this.isAuthenticated,
      }).map((module) => [module.id, module])
    );
  }

  /**
   * Does this module answer the search?
   *
   * Matches the title, the description and the registry keywords, because a person searching for
   * "certificate" should reach Glass Accreditation whether or not that word is in its name. The
   * description is searched even though it is not on screen - it is still what the module IS, and a
   * search that only saw titles would be narrower than the old hub's.
   */
  private answersSearch(module: ResolvedModule, query: string): boolean {
    if (!query) return true;
    const haystack = [
      module.title,
      module.description,
      ...(module.keywords ?? []),
      ...(module.submenu ?? []).map((item) => item.label),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  }

  /** The sections with the search applied. A section with nothing left in it is not rendered. */
  private get rendered(): RenderedSection[] {
    const query = this.search.trim().toLowerCase();
    const byId = this.visibleById;

    return WORKFLOW_SECTIONS.map((section) => ({
      section,
      entries: section.moduleIds
        .map((id) => byId.get(id))
        .filter((module): module is ResolvedModule => !!module)
        .filter((module) => this.answersSearch(module, query))
        .map((module) => ({
          module,
          shortcuts: module.submenu ?? [],
        })),
    })).filter((rendered) => rendered.entries.length > 0);
  }

  /**
   * The matched run of a label, marked.
   *
   * Split rather than replaced, so the label is never parsed as markup - a module title is registry
   * data and a `<mark>` built by string concatenation would be an injection point for it.
   */
  private highlight(label: string): TemplateResult | string {
    const query = this.search.trim();
    if (!query) return label;

    const at = label.toLowerCase().indexOf(query.toLowerCase());
    if (at < 0) return label;

    return html`${label.slice(0, at)}<mark>${label.slice(at, at + query.length)}</mark>${label.slice(at + query.length)}`;
  }

  private renderRecent() {
    if (this.recent.length === 0 || this.search.trim()) return nothing;

    return html`
      <nav class="recent" aria-label="Recently visited">
        <span class="recent-label">Recent</span>
        ${this.recent.map(
          (module) => html`
            <a class="recent-item" href="/${module.route}">
              <sl-icon name=${module.iconName || 'box'}></sl-icon>
              ${module.title}
            </a>
          `
        )}
      </nav>
    `;
  }

  private renderEntry(module: ResolvedModule, shortcuts: ModuleSubmenuItem[]) {
    return html`
      <div class="entry">
        <a class="entry-link" href="/${module.route}"
          >${this.highlight(module.title)}</a
        >
        ${
          module.description
            ? html`
                <!--
                HOIST IS REQUIRED HERE, not a nicety. The sections are laid out with CSS columns,
                and a multi-column container is a fragmentation context: a popup positioned inside
                one anchors against the column fragment rather than against its trigger, which put
                these tooltips in the wrong part of the page entirely - the User Activity
                description appeared beside the search box. Hoisting moves the popup out to a fixed
                positioning strategy so it tracks the icon it belongs to.
              -->
                <sl-tooltip content=${module.description} hoist>
                  <sl-icon
                    class="entry-info"
                    name="info-circle"
                    label=${`About ${module.title}`}
                  ></sl-icon>
                </sl-tooltip>
              `
            : nothing
        }
      </div>
      ${shortcuts.map(
        (item) => html`
          <a class="shortcut" href=${this.shortcutHref(item)}
            >${this.highlight(item.label)}</a
          >
        `
      )}
    `;
  }

  /**
   * Submenu routes in the registry are FULL paths without a leading slash, the same shape as
   * ModuleMeta.route - the modules hub has always rendered them as href="/route". This method
   * used to give slash-less routes a second, module-relative reading; no registry entry ever
   * used that form, and it doubled the module prefix on every shortcut, so "New Product"
   * resolved to a route that matched nothing and fell through to home (found by the architect,
   * 2026-09-05).
   */
  private shortcutHref(item: ModuleSubmenuItem): string {
    return item.route.startsWith('/') ? item.route : `/${item.route}`;
  }

  render() {
    const sections = this.rendered;

    return html`
      <!--
        NO PAGE HEADING. The masthead already says "Menu" directly above this, and a second copy
        four lines below it was the loudest thing on a page whose whole argument is restraint - more
        so once the heading took brand blue. The search takes the leading position instead, which is
        also the first thing anyone using a dense menu reaches for.
      -->
      <div class="menu-header">
        <agwa-search
          class="menu-search"
          size="medium"
          placeholder="Search the menu, or press /"
          .value=${this.search}
          @search-input=${(event: CustomEvent<{ value: string }>) => {
            // `search-input` rather than `search-change`: this filters as you type, which is what
            // the modules hub does and what a dense list needs. `change` fires on commit, which
            // would leave the grid stale until the field was blurred.
            this.search = event.detail?.value ?? '';
          }}
          ${ref((el?: Element) => {
            // Held so the `/` shortcut has something to focus.
            this.searchInput = el as HTMLElement | undefined;
          })}
        ></agwa-search>
      </div>

      ${this.renderRecent()}
      ${
        sections.length === 0
          ? html`<p class="no-results">
              Nothing here matches “${this.search.trim()}”.
            </p>`
          : html`
              <div class="sections">
                ${sections.map(
                  ({ section, entries }) => html`
                    <section>
                      <h2 class="section-title">${section.title}</h2>
                      ${entries.map(({ module, shortcuts }) =>
                        this.renderEntry(module, shortcuts)
                      )}
                    </section>
                  `
                )}
              </div>
            `
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'workflow-menu': WorkflowMenu;
  }
}
